import { useState, useRef, useEffect, useCallback } from "react";

// ── Exported constants (used by App.js / My Day) ────────────────
export const SAMPLE_BLOCKS = [
  { id: 1, start: "9:00",  duration: 90, label: "Deep work - NeuroQuest", type: "deep"    },
  { id: 2, start: "10:30", duration: 30, label: "Team standup",           type: "meeting" },
  { id: 3, start: "11:00", duration: 60, label: "React dev session",      type: "deep"    },
  { id: 4, start: "12:00", duration: 30, label: "Lunch break",            type: "break"   },
  { id: 5, start: "12:30", duration: 60, label: "Investor prep",          type: "task"    },
  { id: 6, start: "1:30",  duration: 30, label: "Admin catch-up",         type: "admin"   },
];

export const TYPE_COLORS = {
  deep:    { bg: "#EFF6FF", border: "#3B82F6", dot: "#3B82F6" },
  task:    { bg: "#F0FDF4", border: "#22C55E", dot: "#22C55E" },
  meeting: { bg: "#FEF3C7", border: "#F59E0B", dot: "#F59E0B" },
  admin:   { bg: "#F5F3FF", border: "#8B5CF6", dot: "#8B5CF6" },
  break:   { bg: "#F8FAFC", border: "#CBD5E1", dot: "#94A3B8" },
};

// ── Helpers ─────────────────────────────────────────────────────
const fmt   = s => `${Math.floor(s/60).toString().padStart(2,"0")}:${(s%60).toString().padStart(2,"0")}`;
const fmtMs = s => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${sec}s`;
  return `${sec}s`;
};

// Clockwise-draining pie path (fraction = how much REMAINS)
function piePath(cx, cy, r, frac) {
  if (frac <= 0) return `M ${cx} ${cy} Z`;
  if (frac >= 0.9999) return `M ${cx} ${cy} m 0 -${r} a ${r} ${r} 0 1 1 -0.001 0 Z`;
  const a = frac * 2 * Math.PI;
  const x = cx + r * Math.sin(a);
  const y = cy - r * Math.cos(a);
  return `M ${cx} ${cy} L ${cx} ${cy - r} A ${r} ${r} 0 ${a > Math.PI ? 1 : 0} 1 ${x} ${y} Z`;
}

// ── Pomodoro presets ─────────────────────────────────────────────
const PRESETS = [
  { label: "5/2",   work: 5,  rest: 2,  tag: "Quick"    },
  { label: "15/3",  work: 15, rest: 3,  tag: "Short"    },
  { label: "25/5",  work: 25, rest: 5,  tag: "Classic"  },
  { label: "45/10", work: 45, rest: 10, tag: "Deep"      },
  { label: "90/20", work: 90, rest: 20, tag: "Ultradian" },
];

const FOCUS_COLOR = "#16A34A";   // solid green
const FOCUS_BG    = "#DCFCE7";   // green tint
const REST_COLOR  = "#2563EB";   // solid blue (not teal)
const REST_BG     = "#DBEAFE";   // blue tint

// ── Single-ring Pomodoro clock with drag-to-scrub ───────────────
function PomClock({ workFrac, restFrac, phase, secs, size, pomRun, onScrub }) {
  const cx = size / 2, cy = size / 2;
  const r     = size / 2 - 10;
  const discR = r * 0.72;
  const isWork = phase === "work";
  const frac   = isWork ? workFrac : restFrac;

  // Only show phase color when actively running
  const activeColor = isWork ? FOCUS_COLOR : REST_COLOR;
  const color = pomRun ? activeColor : "#CBD5E1";
  const bg    = pomRun ? (isWork ? FOCUS_BG : REST_BG) : "#F1F5F9";
  const svgRef = useRef(null);
  const scrubbing = useRef(false);

  const getAngleFrac = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const dx = e.clientX - (rect.left + rect.width/2);
    const dy = e.clientY - (rect.top  + rect.height/2);
    let angle = Math.atan2(dx, -dy) / (2 * Math.PI);
    if (angle < 0) angle += 1;
    return angle;
  };

  const onMouseDown = (e) => { scrubbing.current = true; e.preventDefault(); };
  const onMouseMove = useCallback((e) => {
    if (!scrubbing.current || !onScrub) return;
    onScrub(getAngleFrac(e));
  }, [onScrub]);
  const onMouseUp = useCallback(() => { scrubbing.current = false; }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup",   onMouseUp);
    return () => { window.removeEventListener("mousemove", onMouseMove); window.removeEventListener("mouseup", onMouseUp); };
  }, [onMouseMove, onMouseUp]);

  return (
    <svg ref={svgRef} width={size} height={size} className="block cursor-ew-resize"
      onMouseDown={onMouseDown}>
      {/* Track */}
      <circle cx={cx} cy={cy} r={r} fill={bg} />
      {/* Draining pie */}
      <path d={piePath(cx, cy, r, frac)} fill={color} opacity={pomRun ? 1 : 0.6} />
      {/* Outer glow ring when running */}
      {pomRun && <circle cx={cx} cy={cy} r={r+4} fill="none" stroke={color} strokeWidth={2} opacity={0.25} />}
      {/* Center disc */}
      <circle cx={cx} cy={cy} r={discR} fill="white" />
      {/* Time */}
      <text x={cx} y={cy - size * 0.03} textAnchor="middle" dominantBaseline="middle"
        style={{ fontSize: size * 0.18, fontWeight: 700, fill: pomRun ? color : "#0F172A",
          fontFamily: "'DM Sans',sans-serif", fontVariantNumeric: "tabular-nums" }}>
        {fmt(secs)}
      </text>
      {/* Phase label */}
      <text x={cx} y={cy + size * 0.14} textAnchor="middle" dominantBaseline="middle"
        style={{ fontSize: size * 0.074, fontWeight: 700,
          fill: pomRun ? activeColor : "#94A3B8",
          fontFamily: "'DM Sans',sans-serif", letterSpacing: "0.08em" }}>
        {isWork ? "FOCUS" : "BREAK"}
      </text>
    </svg>
  );
}

// ── Digital pill - clean MM:SS with phase dot ───────────────────
function TwinPill({ workFrac, restFrac, phase, workSecs, restSecs }) {
  const isWork = phase === "work";
  const color  = isWork ? FOCUS_COLOR : REST_COLOR;
  const secs   = isWork ? workSecs : restSecs;

  return (
    <div className="flex items-center gap-[7px]">
      <div className="w-[7px] h-[7px] rounded-full shrink-0" style={{ background: color }} />
      <div className="font-['DM_Sans'] tabular-nums text-[15px] font-bold text-slate-800 tracking-[0.02em]">
        {fmt(secs)}
      </div>
    </div>
  );
}

// ── Flowtime pill clock (simple elapsed fill) ───────────────────
function FlowPillClock({ elapsed, size = 44 }) {
  // Fills up over ~60 min to show time passing
  const frac = Math.min(elapsed / (60 * 60), 1);
  const cx = size / 2, cy = size / 2, r = size / 2 - 3;
  const discR = r * 0.62;
  const a = frac * 2 * Math.PI;
  const path = frac <= 0 ? `M ${cx} ${cy} Z`
    : frac >= 0.9999 ? `M ${cx} ${cy} m 0 -${r} a ${r} ${r} 0 1 1 -0.001 0 Z`
    : `M ${cx} ${cy} L ${cx} ${cy - r} A ${r} ${r} 0 ${a > Math.PI ? 1 : 0} 1 ${cx + r * Math.sin(a)} ${cy - r * Math.cos(a)} Z`;

  return (
    <svg width={size} height={size} className="block">
      <circle cx={cx} cy={cy} r={r} fill="#F1F5F9" />
      <path d={path} fill={FOCUS_COLOR} opacity={0.7} />
      <circle cx={cx} cy={cy} r={discR} fill="white" />
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
        style={{ fontSize: size * 0.185, fontWeight: 700, fill: "#0F172A",
          fontFamily: "'DM Sans',sans-serif" }}>
        {Math.floor(elapsed / 60)}m
      </text>
    </svg>
  );
}

// ── Main FloatingTimer ──────────────────────────────────────────
const PANEL_MIN = 230; // just header + clock
const PANEL_MAX = 520; // full panel

export default function FloatingTimer({ docked, onDock, startExpanded, sharedTimer }) {
  const [expanded,    setExpanded]    = useState(startExpanded !== false); // default open
  const [panelHeight, setPanelHeight] = useState(PANEL_MAX);
  const [tab, setTab]                 = useState("pomodoro");
  const [pos, setPos]                 = useState({ x: window.innerWidth - 310, y: 80 });

  // ── Pomodoro state - use shared if provided, else local ────────
  const [localPhase,   setLocalPhase]   = useState("work");
  const [localSecs,    setLocalSecs]    = useState(25 * 60);
  const [localRun,     setLocalRun]     = useState(false);
  const [preset,       setPreset]       = useState(PRESETS[2]);
  const [cycle,        setCycle]        = useState(1);

  const phase    = sharedTimer ? sharedTimer.pomPhase  : localPhase;
  const setPhase = sharedTimer ? sharedTimer.setPomPhase : setLocalPhase;
  const pomSecs  = sharedTimer ? sharedTimer.pomSecs   : localSecs;
  const setPomSecs = sharedTimer ? sharedTimer.setPomSecs : setLocalSecs;
  const pomRun   = sharedTimer ? sharedTimer.pomRun    : localRun;
  const setPomRun = sharedTimer ? sharedTimer.setPomRun : setLocalRun;

  // Sync preset from shared if available
  useEffect(() => {
    if (sharedTimer?.pomPreset) setPreset(p => ({ ...p, ...sharedTimer.pomPreset }));
  }, []);

  // ── Flowtime state ──────────────────────────────────────────
  const [flowRun,   setFlowRun]   = useState(false);
  const [flowSecs,  setFlowSecs]  = useState(0);
  const [flowLog,   setFlowLog]   = useState([]);
  const [flowNote,  setFlowNote]  = useState("");

  const dragging = useRef(false);
  const offset   = useRef({ x: 0, y: 0 });
  const didDrag  = useRef(false);

  // ── Pomodoro tick (only runs if not using shared timer which has its own tick) ──
  useEffect(() => {
    if (sharedTimer) return; // shared timer ticks in App.js
    if (!pomRun) return;
    const workS = preset.work * 60, restS = preset.rest * 60;
    const t = setInterval(() => setPomSecs(s => {
      if (s <= 1) {
        clearInterval(t);
        if (phase === "work") { setPhase("rest"); setPomSecs(restS); }
        else { setPhase("work"); setPomSecs(workS); setCycle(c => c + 1); }
        return 0;
      }
      return s - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [pomRun, phase, preset, sharedTimer]);

  // ── Flowtime tick ───────────────────────────────────────────
  useEffect(() => {
    if (!flowRun) return;
    const t = setInterval(() => setFlowSecs(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [flowRun]);

  const applyPreset = (p) => {
    if (pomRun) return;
    setPreset(p); setPhase("work"); setPomSecs(p.work * 60); setCycle(1);
    if (sharedTimer) sharedTimer.setPomPreset({ work: p.work, rest: p.rest });
  };

  const stopFlow = () => {
    setFlowRun(false);
    if (flowSecs > 10) {
      setFlowLog(prev => [{ secs: flowSecs, note: flowNote, ts: new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) }, ...prev.slice(0, 4)]);
    }
    setFlowSecs(0);
    setFlowNote("");
  };

  // ── Completion ding + shake ──────────────────────────────────
  const [shaking, setShaking] = useState(false);

  const playDing = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      // Two-tone chime
      [0, 0.18].forEach((delay, i) => {
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = "sine";
        osc.frequency.setValueAtTime(i === 0 ? 880 : 1046, ctx.currentTime + delay);
        gain.gain.setValueAtTime(0, ctx.currentTime + delay);
        gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + delay + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.6);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.65);
      });
    } catch(e) {}
    setShaking(true);
    setTimeout(() => setShaking(false), 600);
  };

  // Watch for phase completion
  const prevSecs = useRef(pomSecs);
  useEffect(() => {
    if (pomRun && prevSecs.current > 2 && pomSecs === 0) playDing();
    prevSecs.current = pomSecs;
  }, [pomSecs, pomRun]);

  // ── Scrub handler (drag clock arc to set time) ───────────────
  const handleScrub = useCallback((frac) => {
    const durS = phase === "work" ? preset.work * 60 : preset.rest * 60;
    setPomSecs(Math.round(frac * durS));
  }, [phase, preset]);

  // ── Drag ────────────────────────────────────────────────────
  const onMouseDown = e => {
    if (e.target.closest("button") || e.target.closest("input") || e.target.closest("textarea")) return;
    dragging.current = true;
    didDrag.current  = false;
    offset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  };
  useEffect(() => {
    const mv = e => {
      if (!dragging.current) return;
      if (Math.abs(e.clientX - (offset.current.x + pos.x)) > 4 ||
          Math.abs(e.clientY - (offset.current.y + pos.y)) > 4) {
        didDrag.current = true;
      }
      const newY = e.clientY - offset.current.y;
      setPos({ x: e.clientX - offset.current.x, y: newY });
    };
    const up = e => {
      if (dragging.current && didDrag.current && (e.clientY - offset.current.y) < 60) {
        onDock(); // dragged to top bar area
      }
      dragging.current = false;
    };
    window.addEventListener("mousemove", mv); window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", mv); window.removeEventListener("mouseup", up); };
  }, [onDock]);

  // ── Resize handle ────────────────────────────────────────────
  const resizing     = useRef(false);
  const resizeStartY = useRef(0);
  const resizeStartH = useRef(PANEL_MAX);

  const onResizeStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    resizing.current     = true;
    resizeStartY.current = e.clientY;
    resizeStartH.current = panelHeight;
  };

  useEffect(() => {
    const mv = e => {
      if (!resizing.current) return;
      const delta  = e.clientY - resizeStartY.current;
      const newH   = Math.max(PANEL_MIN, Math.min(PANEL_MAX, resizeStartH.current + delta));
      setPanelHeight(newH);
    };
    const up = () => { resizing.current = false; };
    window.addEventListener("mousemove", mv);
    window.addEventListener("mouseup",   up);
    return () => { window.removeEventListener("mousemove", mv); window.removeEventListener("mouseup", up); };
  }, [panelHeight]);


  const workDurS = preset.work * 60;
  const restDurS = preset.rest * 60;
  const workFrac = phase === "work" ? pomSecs / workDurS : 0;
  const restFrac = phase === "rest" ? pomSecs / restDurS : 1;
  // Seconds to show on each pill pie
  const pillWorkSecs = phase === "work" ? pomSecs : 0;
  const pillRestSecs = phase === "rest" ? pomSecs : preset.rest * 60;

  if (docked) return null;

  return (
    <div onMouseDown={onMouseDown}
      className="fixed z-[60] select-none cursor-grab font-['DM_Sans']"
      style={{
        left: pos.x,
        top: pos.y,
        animation: shaking ? "shake 0.5s ease-in-out" : "none"
      }}>

      {/* ── COLLAPSED PILL ── */}
      {!expanded && (
        <div
          className="flex items-center gap-2 px-[10px] py-[6px] pl-[14px] backdrop-blur-xl border border-black/[0.1] rounded-[28px] shadow-lg cursor-grab transition-[background] duration-[400ms]"
          style={{
            background: tab === "pomodoro"
              ? (phase === "work"
                  ? (pomRun ? "#16A34A" : "rgba(255,255,255,0.96)")
                  : (pomRun ? "#2563EB" : "rgba(255,255,255,0.96)"))
              : "rgba(255,255,255,0.96)"
          }}>

          {/* Time + label */}
          <div onClick={() => { if (!didDrag.current) setExpanded(true); }}
            className="flex items-center gap-2 cursor-pointer">
            {tab === "pomodoro" ? (
              <>
                <div className="w-2 h-2 rounded-full shrink-0 transition-all duration-300"
                  style={{
                    background: !pomRun ? "#CBD5E1" : phase === "work" ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.9)",
                    boxShadow: pomRun ? "0 0 6px rgba(255,255,255,0.8)" : "none"
                  }} />
                <div className="font-['DM_Sans'] tabular-nums text-[15px] font-bold tracking-[0.01em] transition-colors duration-300"
                  style={{ color: pomRun ? "white" : "#1E293B" }}>
                  {fmt(pomSecs)}
                </div>
                {pomRun && (
                  <div className="text-[10px] font-bold text-white/80 uppercase tracking-[0.06em]">
                    {phase === "work" ? "Focus" : "Break"}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="w-2 h-2 rounded-full shrink-0"
                  style={{ background: flowRun ? "white" : "#CBD5E1" }} />
                <div className="font-['DM_Sans'] tabular-nums text-[15px] font-bold"
                  style={{ color: flowRun ? "white" : "#1E293B" }}>
                  {fmt(flowSecs)}
                </div>
                {flowRun && (
                  <div className="text-[10px] font-bold text-white/80 uppercase tracking-[0.06em]">
                    Flow
                  </div>
                )}
              </>
            )}
          </div>

          {/* Controls - play/pause + expand + dock */}
          <div className="flex items-center gap-[2px] ml-[2px]">
            <button
              onClick={e => { e.stopPropagation(); tab === "pomodoro" ? setPomRun(r => !r) : (flowRun ? stopFlow() : setFlowRun(true)); }}
              className="w-[26px] h-[26px] rounded-full border-none cursor-pointer text-[10px] flex items-center justify-center"
              style={{
                background: pomRun ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.06)",
                color: pomRun ? "white" : "#64748B"
              }}>
              {(tab === "pomodoro" ? pomRun : flowRun) ? "⏸" : "▶"}
            </button>
            {/* Expand ▼ */}
            <button
              onClick={e => { e.stopPropagation(); setExpanded(true); }}
              className="w-[26px] h-[26px] rounded-full border-none cursor-pointer bg-transparent text-[10px] flex items-center justify-center"
              style={{ color: pomRun ? "rgba(255,255,255,0.5)" : "#CBD5E1" }}>
              ▼
            </button>
            {/* Dock ✕ */}
            <button
              onClick={e => { e.stopPropagation(); onDock(); }}
              className="w-[26px] h-[26px] rounded-full border-none cursor-pointer bg-transparent text-[12px] flex items-center justify-center"
              style={{ color: pomRun ? "rgba(255,255,255,0.5)" : "#CBD5E1" }}>
              ✕
            </button>
          </div>
        </div>
      )}

      {/* ── EXPANDED PANEL ── */}
      {expanded && (
        <div className="w-[272px] bg-white/98 backdrop-blur-xl border border-black/[0.09] rounded-[20px] shadow-lg overflow-hidden flex flex-col"
          style={{ height: panelHeight }}>

          {/* Header */}
          <div className="flex items-center gap-2 px-3 py-[10px] pb-2">
            <div className="flex flex-1 bg-slate-100 rounded-[9px] p-[3px] gap-[2px]">
              {[{ id: "pomodoro", icon: "🍅", label: "Pomodoro" },
                { id: "flowtime", icon: "🌊", label: "Flowtime" }].map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className="flex-1 py-1 rounded-[7px] border-none text-[10px] font-semibold cursor-pointer font-['DM_Sans']"
                  style={{
                    background: tab === t.id ? "white" : "transparent",
                    color: tab === t.id ? "#1E293B" : "#94A3B8",
                    boxShadow: tab === t.id ? "0 1px 3px rgba(0,0,0,0.08)" : "none"
                  }}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>
            <button onClick={onDock} title="Dock to nav bar"
              className="bg-transparent border-none text-slate-300 text-[14px] cursor-pointer px-[3px] py-[2px] leading-none">⊙</button>
            <button onClick={() => setExpanded(false)}
              className="bg-transparent border-none text-slate-300 text-[14px] cursor-pointer px-[3px] py-[2px] leading-none">▲</button>
          </div>

          {/* Scrollable tab content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
          {/* ── POMODORO TAB ── */}
          {tab === "pomodoro" && (
            <div className="pb-[14px]">

              {/* Clock */}
              <div className="flex justify-center pt-[6px]">
                <PomClock workFrac={workFrac} restFrac={restFrac} phase={phase}
                  secs={pomSecs} size={152} pomRun={pomRun} onScrub={handleScrub} />
              </div>

              {/* Cycle + end time */}
              <div className="text-center text-[9px] text-slate-400 font-['DM_Sans'] mb-3">
                {pomRun
                  ? `Cycle ${cycle} · done at ${new Date(Date.now() + pomSecs * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
                  : `Cycle ${cycle} · ${preset.work}min focus, ${preset.rest}min break`}
              </div>

              {/* Presets */}
              <div className="px-[14px] pb-[10px]">
                <div className="text-[8px] font-bold text-slate-300 uppercase tracking-[0.07em] mb-[6px] font-['DM_Sans']">Quick Set</div>
                <div className="flex gap-[3px] mb-2">
                  {PRESETS.map(p => {
                    const active = preset.label === p.label;
                    return (
                      <button key={p.label} onClick={() => applyPreset(p)}
                        className="flex-1 py-[5px] px-[2px] rounded-lg text-center font-['DM_Sans']"
                        style={{
                          border: `1px solid ${active ? FOCUS_COLOR : "rgba(0,0,0,0.08)"}`,
                          background: active ? `${FOCUS_COLOR}15` : "#F8FAFC",
                          cursor: pomRun ? "default" : "pointer",
                          opacity: pomRun ? 0.4 : 1
                        }}>
                        <div className="text-[9px] font-bold"
                          style={{ color: active ? FOCUS_COLOR : "#1E293B" }}>{p.label}</div>
                        <div className="text-[7px] text-slate-400 mt-[1px]">{p.tag}</div>
                      </button>
                    );
                  })}
                </div>

                {/* Custom row */}
                <div className="flex items-center gap-[6px]">
                  <div className="text-[8px] text-slate-400 font-['DM_Sans'] shrink-0">Custom</div>
                  <div className="flex items-center gap-1 flex-1">
                    <input type="number" min={1} max={180}
                      value={preset.work}
                      onChange={e => { if (!pomRun) applyPreset({ label: "custom", work: Math.max(1, +e.target.value || 1), rest: preset.rest, tag: "Custom" }); }}
                      className="w-fit px-[5px] py-[3px] rounded-md border border-black/[0.12] text-[11px] font-semibold text-center outline-none font-['DM_Sans'] bg-slate-50"
                      style={{ color: FOCUS_COLOR }}
                    />
                    <span className="text-[9px] text-slate-300 font-['DM_Sans']">work</span>
                    <span className="text-[9px] text-slate-300">/</span>
                    <input type="number" min={1} max={60}
                      value={preset.rest}
                      onChange={e => { if (!pomRun) applyPreset({ label: "custom", work: preset.work, rest: Math.max(1, +e.target.value || 1), tag: "Custom" }); }}
                      className="w-fit px-[5px] py-[3px] rounded-md border border-black/[0.12] text-[11px] font-semibold text-center outline-none font-['DM_Sans'] bg-slate-50"
                      style={{ color: REST_COLOR }}
                    />
                    <span className="text-[9px] text-slate-300 font-['DM_Sans']">break</span>
                    <span className="text-[8px] text-slate-300 font-['DM_Sans']">(min)</span>
                  </div>
                </div>
              </div>

              {/* Start / Pause */}
              <div className="px-[14px]">
                <button onClick={() => setPomRun(r => !r)}
                  className="w-full p-[10px] rounded-xl border-none text-white text-[13px] font-bold cursor-pointer font-['DM_Sans'] flex items-center justify-center gap-[7px]"
                  style={{ background: pomRun ? "#EF4444" : "#4DB8B5" }}>
                  {pomRun ? "⏸ Pause" : "▶ Start"}
                </button>
                <div className="text-center text-[8px] text-slate-300 font-['DM_Sans'] mt-[7px]">
                  Soft chime when time's up. No alarm.
                </div>
              </div>
            </div>
          )}

          {/* ── FLOWTIME TAB ── */}
          {tab === "flowtime" && (
            <div className="pb-[14px]">

              {/* Elapsed display */}
              <div className="flex flex-col items-center py-4 pt-4 pb-2 gap-1">
                <div className="text-[42px] font-bold text-slate-900 font-['DM_Sans'] tabular-nums leading-none">
                  {fmt(flowSecs)}
                </div>
                <div className="text-[9px] font-bold font-['DM_Sans'] uppercase tracking-[0.08em]"
                  style={{ color: flowRun ? FOCUS_COLOR : "#94A3B8" }}>
                  {flowRun ? "In flow" : flowSecs > 0 ? "Paused" : "Ready"}
                </div>
              </div>

              {/* What are you working on - only when not running */}
              {!flowRun && flowSecs === 0 && (
                <div className="px-[14px] pb-3">
                  <input
                    value={flowNote}
                    onChange={e => setFlowNote(e.target.value)}
                    placeholder="What are you working on? (optional)"
                    className="w-full px-[10px] py-[7px] rounded-lg border border-black/[0.1] text-[11px] text-slate-800 font-['DM_Sans'] outline-none box-border bg-slate-50"
                  />
                </div>
              )}

              {/* Start / Stop */}
              <div className="px-[14px]">
                {!flowRun ? (
                  <button onClick={() => setFlowRun(true)}
                    className="w-full p-[10px] rounded-xl border-none text-white text-[13px] font-bold cursor-pointer font-['DM_Sans']"
                    style={{ background: FOCUS_COLOR }}>
                    ▶ Start flow
                  </button>
                ) : (
                  <button onClick={stopFlow}
                    className="w-full p-[10px] rounded-xl border-none bg-red-500 text-white text-[13px] font-bold cursor-pointer font-['DM_Sans']">
                    ⏹ Done - log session
                  </button>
                )}
              </div>

              {/* What is flowtime */}
              {!flowRun && flowSecs === 0 && flowLog.length === 0 && (
                <div className="mx-[14px] mt-3 px-[10px] py-2 bg-slate-50 rounded-lg border border-black/[0.06]">
                  <div className="text-[10px] font-semibold text-slate-600 font-['DM_Sans'] mb-[3px]">
                    How Flowtime works
                  </div>
                  <div className="text-[9px] text-slate-400 font-['DM_Sans'] leading-relaxed">
                    No fixed intervals. Start when you begin. Stop when your focus breaks naturally.
                    Built for hyperfocus — work as long as you're in it.
                  </div>
                </div>
              )}

              {/* Session log */}
              {flowLog.length > 0 && (
                <div className="mx-[14px] mt-3">
                  <div className="text-[8px] font-bold text-slate-300 uppercase tracking-[0.07em] mb-[6px] font-['DM_Sans']">
                    Today's sessions
                  </div>
                  {flowLog.map((s, i) => (
                    <div key={i} className="flex items-center justify-between px-2 py-[5px] rounded-[7px] mb-[2px]"
                      style={{ background: i === 0 ? `${FOCUS_COLOR}0D` : "transparent" }}>
                      <div className="text-[10px] text-slate-700 font-['DM_Sans'] overflow-hidden text-ellipsis whitespace-nowrap max-w-[140px]">
                        {s.note || "Flow session"}
                      </div>
                      <div className="flex gap-[6px] items-center shrink-0">
                        <span className="text-[10px] font-bold font-['DM_Sans']"
                          style={{ color: FOCUS_COLOR }}>
                          {fmtMs(s.secs)}
                        </span>
                        <span className="text-[8px] text-slate-300 font-['DM_Sans']">
                          {s.ts}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          </div> {/* end scrollable content */}

          {/* Resize handle */}
          <div
            onMouseDown={onResizeStart}
            className="h-3 cursor-ns-resize flex items-center justify-center shrink-0 bg-black/[0.02] border-t border-black/[0.05] rounded-b-[20px]">
            <div className="w-8 h-[3px] rounded-sm bg-black/[0.12]" />
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.35}}
        @keyframes shake{0%,100%{transform:translateX(0)}15%{transform:translateX(-6px)}30%{transform:translateX(6px)}45%{transform:translateX(-5px)}60%{transform:translateX(5px)}75%{transform:translateX(-3px)}90%{transform:translateX(3px)}}
      `}</style>
    </div>
  );
}
